import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../api";

const DECLINE = { key: "decline", label: "Can't make it", color: "#6b7280" };

// Multi-tenant, no-login public signup page -- any society's slug works here,
// unlike the Pin-High-only, PIN-gated EventAttendance.jsx. Player
// identification reuses the existing GET /api/terminal/:slug/search
// surname-search endpoint rather than any new auth mechanism, per the plan.
export default function PlannerSignup() {
  const { slug } = useParams();
  const [fixtures, setFixtures] = useState([]);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [surname, setSurname] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [player, setPlayer] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [myBuggy, setMyBuggy] = useState(false);
  const [error, setError] = useState("");
  const [myPayment, setMyPayment] = useState(null);
  const [onlinePaymentAvailable, setOnlinePaymentAvailable] = useState(false);
  const [payingNow, setPayingNow] = useState(false);

  useEffect(() => {
    api.get(`/planner/public/${slug}`).then(r => setFixtures(r.data)).catch(() => setError("Couldn't load fixtures for this society."));
  }, [slug]);

  const search = async (q) => {
    setSurname(q); setPlayer(null);
    if (q.trim().length < 2) { setMatches([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/terminal/${slug}/search`, { params: { q } });
      setMatches(res.data);
    } catch(e) { setMatches([]); }
    finally { setSearching(false); }
  };

  const respond = async (choice, buggy) => {
    setSending(true); setError("");
    try {
      const res = await api.post(`/planner/public/${slug}/signup`, {
        event_id: selectedFixture.id, player_id: player.id, choice, buggy: !!buggy,
      });
      setResult(res.data);
      setMyBuggy(choice === "decline" ? false : !!buggy);
      // Balance only exists once confirmed -- reserve/declined never get a
      // payment row (Mike's §11), and this fixture may have Payments off
      // entirely, in which case the backend just returns { payment: null }.
      if (res.data.attendance_status === "confirmed" && selectedFixture.payments_required) {
        try {
          const payRes = await api.post(`/planner/public/${slug}/${selectedFixture.id}/payments/mine`, { player_id: player.id });
          setMyPayment(payRes.data.payment);
          setOnlinePaymentAvailable(!!payRes.data.online_payment_available);
        } catch(e) { /* non-critical -- signup itself already succeeded */ }
      } else {
        setMyPayment(null);
      }
    } catch(e) { setError(e.response?.data?.error || "Something went wrong — please try again."); }
    finally { setSending(false); }
  };

  const payNow = async () => {
    if (!myPayment) return;
    setPayingNow(true);
    try {
      // Generated once per click, before the request fires -- if a
      // double-click or double-fire sends two near-identical requests,
      // both carry the SAME key, so Stripe's own idempotency collapses
      // them into a single real Checkout Session instead of two.
      const idempotencyKey = crypto.randomUUID();
      const res = await api.post(`/planner/${selectedFixture.id}/payments/${myPayment.payment_id}/checkout`, {
        player_id: player.id, idempotency_key: idempotencyKey,
      });
      window.location.href = res.data.url;
    } catch(e) { setError(e.response?.data?.error || "Couldn't start payment — please try again."); setPayingNow(false); }
  };

  const Card = ({ children }) => (
    <div style={{ background: "white", borderRadius: "16px", padding: "28px 24px", maxWidth: "420px", width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>{children}</div>
  );

  if (result) {
    const message = result.attendance_status === "reserve"
      ? `The event is full, so you've been added to the reserve list (#${result.reserve_position}) — we'll be in touch if a place opens up.`
      : result.attendance_status === "declined"
      ? "Thanks for letting us know — we've recorded that you can't make it."
      : `You're signed up${myBuggy ? " with a buggy" : ""}! See you there.`;
    const closed = selectedFixture.effective_status === "closed";
    return (
      <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <Card>
          <div style={{ fontSize: "40px", marginBottom: "12px", textAlign: "center" }}>⛳</div>
          <div style={{ color: "#166534", fontSize: "16px", textAlign: "center", marginBottom: "16px" }}>{message}</div>
          {myPayment && (
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px", marginBottom: "16px", fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>Total due</span><span style={{ fontWeight: 700 }}>£{myPayment.amount_due.toFixed(2)}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>Paid so far</span><span style={{ fontWeight: 700 }}>£{myPayment.amount_paid.toFixed(2)}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, borderTop: "1px solid #e5e7eb" }}>
                <span style={{ fontWeight: 700 }}>Balance</span>
                <span style={{ fontWeight: 800, color: myPayment.balance <= 0 ? "#166534" : "#92400e" }}>
                  {myPayment.balance <= 0 ? "✓ Paid in full" : `£${myPayment.balance.toFixed(2)}`}
                </span>
              </div>
              {selectedFixture.payment_deadline && myPayment.balance > 0 && (
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>
                  Please pay by {new Date(selectedFixture.payment_deadline).toLocaleDateString("en-GB")}.
                  {!onlinePaymentAvailable && " Contact your organiser to pay — online payment isn't available yet."}
                </div>
              )}
              {myPayment.balance > 0 && onlinePaymentAvailable && (
                <button onClick={payNow} disabled={payingNow}
                  style={{ width: "100%", background: "#1d4ed8", color: "white", border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 10, opacity: payingNow ? 0.6 : 1 }}>
                  {payingNow ? "Redirecting to secure payment…" : `Pay £${myPayment.balance.toFixed(2)} Now`}
                </button>
              )}
            </div>
          )}
          {!closed && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {result.attendance_status === "declined" ? (
                <>
                  <button disabled={sending} onClick={() => respond("signup", false)}
                    style={{ background: "#166534", color: "white", border: "none", borderRadius: 10, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: sending?0.6:1 }}>
                    Sign Up
                  </button>
                  <button disabled={sending} onClick={() => respond("signup", true)}
                    style={{ background: "#1d4ed8", color: "white", border: "none", borderRadius: 10, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: sending?0.6:1 }}>
                    Buggy Required
                  </button>
                </>
              ) : (
                <>
                  {!myBuggy && (
                    <button disabled={sending} onClick={() => respond("signup", true)}
                      style={{ background: "#1d4ed8", color: "white", border: "none", borderRadius: 10, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: sending?0.6:1 }}>
                      + Add Buggy
                    </button>
                  )}
                  <button disabled={sending} onClick={() => respond(DECLINE.key)}
                    style={{ background: "none", color: DECLINE.color, border: "1px solid #d1d5db", borderRadius: 10, padding: "10px", fontSize: 13, cursor: "pointer" }}>
                    Can't make it
                  </button>
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <Card>
        {!selectedFixture && (
          <>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "16px", textAlign: "center" }}>Upcoming Fixtures</div>
            {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}
            {fixtures.length === 0 && !error && <div style={{ color: "#6b7280", fontSize: "13px" }}>No fixtures open for signup right now.</div>}
            {fixtures.map(f => (
              <div key={f.id} onClick={() => setSelectedFixture(f)}
                style={{ padding: "14px", border: "1px solid #d1fae5", borderRadius: "10px", marginBottom: "10px", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ fontWeight: 700, fontSize: "14px", color: "#14532d" }}>{f.event_name}</div>
                  {f.effective_status === "closed" && <span style={{ background: "#fef3c7", color: "#92400e", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "999px" }}>CLOSED</span>}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>{new Date(f.event_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} · {f.venue}</div>
              </div>
            ))}
          </>
        )}

        {selectedFixture && !player && (
          <>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px", textAlign: "center" }}>{selectedFixture.event_name}</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px", textAlign: "center" }}>{new Date(selectedFixture.event_date).toLocaleDateString("en-GB")} · {selectedFixture.venue}</div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>Type your surname</label>
            <input value={surname} onChange={e => search(e.target.value)} placeholder="e.g. Sellman" autoFocus
              style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" }} />
            {searching && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>Searching…</div>}
            {matches.map(m => (
              <div key={m.id} onClick={() => setPlayer(m)}
                style={{ padding: "10px 12px", marginTop: 8, border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer", fontSize: 14 }}>
                {m.name}
              </div>
            ))}
          </>
        )}

        {selectedFixture && player && (
          <>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "#14532d", marginBottom: "16px", textAlign: "center" }}>Hi {player.name.split(" ")[1] || player.name} — are you coming?</div>
            {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}
            {(selectedFixture.golf_cost != null || selectedFixture.dinner_cost != null || selectedFixture.buggy_cost != null) && (
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "14px", textAlign: "center" }}>
                {selectedFixture.golf_cost != null && <>Golf £{selectedFixture.golf_cost}  </>}
                {selectedFixture.dinner_cost != null && <>Dinner £{selectedFixture.dinner_cost}  </>}
                {selectedFixture.buggy_cost != null && <>Buggy £{selectedFixture.buggy_cost}</>}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button disabled={sending} onClick={() => respond("signup", false)}
                style={{ background: "#166534", color: "white", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: sending?0.6:1 }}>
                Sign Up
              </button>
              <button disabled={sending} onClick={() => respond("signup", true)}
                style={{ background: "#1d4ed8", color: "white", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: sending?0.6:1 }}>
                Buggy Required
              </button>
              <button disabled={sending} onClick={() => respond(DECLINE.key)}
                style={{ background: "none", color: DECLINE.color, border: "1px solid #d1d5db", borderRadius: 10, padding: "10px", fontSize: 13, cursor: "pointer" }}>
                {DECLINE.label}
              </button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
